import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { CalendarClock, Loader2, RotateCcw, Send } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { useConfirm } from '@orisini/ui/components/confirm'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type Workspace } from '../../accounts/lib/accounts'
import { ContentList } from '../components/ContentList'
import { contentApi, type ContentItem } from '../lib/content'

const FILTERS = [
  ['all', 'All'],
  ['working', 'In progress'],
  ['ready', 'Ready'],
  ['scheduled', 'Scheduled'],
  ['published', 'Published'],
  ['failed', 'Failed'],
] as const

/** Selang penyegaran selama ada konten yang sedang ditulis/diproduksi. */
const POLL_MS = 6000

/**
 * /bank — Content Bank: semua yang sudah (atau sedang) diproduksi, dengan
 * status produksi dan status publikasi yang terpisah.
 */
export default function BankPage() {
  const confirm = useConfirm()
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? 'all'
  const account = params.get('account') ?? ''
  const [items, setItems] = useState<ContentItem[] | null>(null)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      setItems((await contentApi.bank(status, account || undefined)).items)
    } catch (err) {
      setItems([])
      toast.error(err instanceof Error ? err.message : "Couldn't load the Content Bank")
    }
  }, [status, account])

  useEffect(() => {
    setItems(null)
    void muat()
  }, [muat])
  useEffect(() => {
    accountsApi.workspaces().then(setWorkspaces).catch(() => undefined)
  }, [])

  // Selama ada yang sedang dikerjakan, segarkan berkala supaya hasilnya muncul sendiri.
  const sibuk = items?.some((i) => ['drafting', 'approved', 'producing'].includes(i.productionStatus)) ?? false
  useEffect(() => {
    if (!sibuk) return
    const t = setInterval(() => void muat(), POLL_MS)
    return () => clearInterval(t)
  }, [sibuk, muat])

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  const aksi = async (it: ContentItem, action: 'queue' | 'publish' | 'render' | 'regenerate') => {
    if (action === 'publish') {
      const ok = await confirm({ title: 'Post this now?', description: `It goes to ${it.workspaceName} on ${it.platform} as soon as Orisini Agent picks it up.`, confirmLabel: 'Post now' })
      if (!ok) return
    }
    setBusy(`${action}:${it.id}`)
    try {
      await contentApi.act(it.id, action)
      toast.success({ queue: 'Added to the queue', publish: 'Sent to post now', render: 'Rendering again', regenerate: 'Rewriting the draft' }[action])
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
      await muat()
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Content Bank</h1>
          <p className="text-sm text-muted-foreground">Everything Orisini has produced for your projects. Schedule, post, or download it from here.</p>
        </div>
        {workspaces.length > 1 && (
          <select aria-label="Filter by project" className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={account} onChange={(e) => set('account', e.target.value)}>
            <option value="">All projects</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}
      </header>

      <div className="flex flex-wrap gap-1 rounded-md bg-muted p-1 w-fit">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => set('status', k === 'all' ? '' : k)} className={cn('rounded px-3 py-1.5 text-sm', status === k ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground')}>
            {label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing here yet. Pick stories in <Link to="/discover" className="text-foreground underline-offset-4 hover:underline">Discover</Link>, or start from your
          own material in <Link to="/create" className="text-foreground underline-offset-4 hover:underline">Create</Link>.
        </p>
      ) : (
        <ContentList
          items={items}
          actions={(it) => {
            const tunggu = busy?.endsWith(it.id)
            if (it.productionStatus === 'ready' && (it.publishStatus === 'none' || it.publishStatus === 'awaiting_approval' || it.publishStatus === 'failed')) {
              // Project tanpa akun, atau carousel untuk Pinterest: hanya bisa diunduh.
              const unduhSaja = !it.productCode || (it.productCode === 'pinboss' && it.kind === 'carousel')
              return unduhSaja ? (
                <Button size="sm" asChild variant="outline">
                  <Link to={`/content/${it.id}`}>Download</Link>
                </Button>
              ) : (
                <>
                  <Button size="sm" disabled={!!busy} onClick={() => aksi(it, 'queue')}>
                    {tunggu ? <Loader2 className="animate-spin" /> : <CalendarClock />} Add to queue
                  </Button>
                  <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => aksi(it, 'publish')}>
                    <Send /> Post now
                  </Button>
                </>
              )
            }
            if (it.productionStatus === 'failed') {
              return (
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => aksi(it, it.caption === null ? 'regenerate' : 'render')}>
                  {tunggu ? <Loader2 className="animate-spin" /> : <RotateCcw />} Try again
                </Button>
              )
            }
            return (
              <Button size="sm" asChild variant="ghost">
                <Link to={`/content/${it.id}`}>Open</Link>
              </Button>
            )
          }}
        />
      )}
    </div>
  )
}
